import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import {
  useCampeonatos, useCreateCampeonato, useUpdateCampeonato, useDeleteCampeonato,
  useRegrasValor, useCreateRegraValor, useDeleteRegraValor,
} from '../hooks/useCampeonatos'
import { useClubes } from '../../clubes/hooks/useClubes'
import { campeonatoFormSchema, regraValorSchema, type CampeonatoFormData, type RegraValorFormData } from '../schemas'
import { useDebounce } from '../../../hooks/useDebounce'
import type { Campeonato, RegraValorInscricao } from '../types'

function FlagCheckbox({ label, name, register }: { label: string; name: any; register: any }) {
  return (
    <label className="flex items-center gap-2 text-sm text-compumaq-texto cursor-pointer">
      <input type="checkbox" {...register(name)} className="rounded border-compumaq-dourado/25 text-compumaq-doradoClaro" />
      {label}
    </label>
  )
}

export function CampeonatosPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({ descricao: '', temporada: '', idclube: '' })
  const debouncedFilters = useDebounce(filters, 400)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Campeonato | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Campeonato | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [regrasModal, setRegrasModal] = useState<number | null>(null)

  const { data, isLoading } = useCampeonatos({
    descricao: debouncedFilters.descricao || undefined,
    temporada: debouncedFilters.temporada ? parseInt(debouncedFilters.temporada) : undefined,
    idclube: debouncedFilters.idclube ? parseInt(debouncedFilters.idclube) : undefined,
    page,
    limit: 20,
  })
  const { data: clubesData } = useClubes({ limit: 200 })
  const createCampeonato = useCreateCampeonato()
  const updateCampeonato = useUpdateCampeonato()
  const deleteCampeonato = useDeleteCampeonato()

  const form = useForm<CampeonatoFormData>({
    resolver: zodResolver(campeonatoFormSchema),
    defaultValues: {
      idclube: 0, descricao: '', numero_etapa: 1, temporada: new Date().getFullYear(),
      flag_soma_etapa: false, flag_calcula_somatoria_auto: false, flag_descarta_etapa: false,
      numero_descarte_etapa: 0, flag_obriga_arma_inscricao: false, flag_permite_reinscricao: false,
      numero_max_inscricao: 1,
    },
  })

  function openCreate() {
    setEditing(null)
    form.reset({
      idclube: 0, descricao: '', numero_etapa: 1, temporada: new Date().getFullYear(),
      flag_soma_etapa: false, flag_calcula_somatoria_auto: false, flag_descarta_etapa: false,
      numero_descarte_etapa: 0, flag_obriga_arma_inscricao: false, flag_permite_reinscricao: false,
      numero_max_inscricao: 1,
    })
    setModalOpen(true)
  }

  function openEdit(c: Campeonato) {
    setEditing(c)
    form.reset({
      idclube: Number(c.idclube), descricao: c.descricao, numero_etapa: Number(c.numero_etapa),
      temporada: Number(c.temporada), flag_soma_etapa: c.flag_soma_etapa,
      flag_calcula_somatoria_auto: c.flag_calcula_somatoria_auto,
      flag_descarta_etapa: c.flag_descarta_etapa, numero_descarte_etapa: Number(c.numero_descarte_etapa),
      flag_obriga_arma_inscricao: c.flag_obriga_arma_inscricao,
      flag_permite_reinscricao: c.flag_permite_reinscricao,
      numero_max_inscricao: Number(c.numero_max_inscricao),
    })
    setModalOpen(true)
  }

  async function handleSubmit(formData: CampeonatoFormData) {
    try {
      if (editing) {
        await updateCampeonato.mutateAsync({ id: editing.idcampeonato, data: formData })
        setAlert({ type: 'success', message: 'Campeonato atualizado com sucesso' })
      } else {
        await createCampeonato.mutateAsync(formData as any)
        setAlert({ type: 'success', message: 'Campeonato cadastrado com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar campeonato' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteCampeonato.mutateAsync(deleteTarget.idcampeonato)
      setAlert({ type: 'success', message: 'Campeonato excluido com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir campeonato' })
    } finally {
      setDeleteTarget(null)
    }
  }

  const meta = data?.meta
  const isSaving = createCampeonato.isPending || updateCampeonato.isPending

  return (
    <div className="space-y-4">
      <PageHeader
        title="Campeonatos"
        description="Gerenciar campeonatos"
        action={
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            + Novo Campeonato
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Descricao</label>
            <input className={inputClass} placeholder="Filtrar..." value={filters.descricao}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, descricao: e.target.value })) }} />
          </div>
          <div className="w-28">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Temporada</label>
            <input className={inputClass} placeholder="2025" type="number" value={filters.temporada}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, temporada: e.target.value })) }} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Clube</label>
            <select className={selectClass} value={filters.idclube}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, idclube: e.target.value })) }}>
              <option value="">Todos</option>
              {clubesData?.data?.map(c => <option key={c.idclube} value={c.idclube}>{c.nome}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setFilters({ descricao: '', temporada: '', idclube: '' }); setPage(1) }}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Limpar</button>
          </div>
        </div>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🏆</p>
            <p className="font-medium">Nenhum campeonato encontrado</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Descricao</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Temporada</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Clube</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Etapas</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((c) => (
                  <tr key={c.idcampeonato} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 font-medium text-compumaq-texto">{c.descricao}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{c.temporada}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{c.clube_nome}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{c.numero_etapa}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setRegrasModal(c.idcampeonato)}
                          className="text-xs text-purple-600 hover:text-purple-800 font-medium">Regras $</button>
                        <button onClick={() => navigate(`/modalidades?idcampeonato=${c.idcampeonato}`)}
                          className="text-xs text-green-600 hover:text-green-800 font-medium">Modalidades</button>
                        <button onClick={() => openEdit(c)}
                          className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                        <button onClick={() => setDeleteTarget(c)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Campeonato' : 'Novo Campeonato'} size="xl">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Clube" required error={form.formState.errors.idclube?.message}>
            <Controller control={form.control} name="idclube" render={({ field }) => (
              <select className={selectClass} value={field.value || ''} onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}>
                <option value="">Selecione...</option>
                {clubesData?.data?.map(c => <option key={c.idclube} value={c.idclube}>{c.nome}</option>)}
              </select>
            )} />
          </FormField>
          <FormField label="Descricao" required error={form.formState.errors.descricao?.message}>
            <input className={inputClass} {...form.register('descricao')} placeholder="Descricao do campeonato" />
          </FormField>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Temporada" required error={form.formState.errors.temporada?.message}>
              <input className={inputClass} type="number" {...form.register('temporada', { valueAsNumber: true })} />
            </FormField>
            <FormField label="Numero de Etapas" error={form.formState.errors.numero_etapa?.message}>
              <input className={inputClass} type="number" min={1} {...form.register('numero_etapa', { valueAsNumber: true })} />
            </FormField>
            <FormField label="Max. inscricoes" error={form.formState.errors.numero_max_inscricao?.message}>
              <input className={inputClass} type="number" min={1} {...form.register('numero_max_inscricao', { valueAsNumber: true })} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-compumaq-texto">Configuracoes</p>
              <FlagCheckbox label="Soma etapas" name="flag_soma_etapa" register={form.register} />
              <FlagCheckbox label="Calcula somatoria auto" name="flag_calcula_somatoria_auto" register={form.register} />
              <FlagCheckbox label="Descarta etapas" name="flag_descarta_etapa" register={form.register} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-compumaq-texto">Inscricoes</p>
              <FlagCheckbox label="Obriga arma na inscricao" name="flag_obriga_arma_inscricao" register={form.register} />
              <FlagCheckbox label="Permite reinscricao" name="flag_permite_reinscricao" register={form.register} />
              <FormField label="Num. descartes" error={form.formState.errors.numero_descarte_etapa?.message}>
                <input className={inputClass} type="number" min={0} {...form.register('numero_descarte_etapa', { valueAsNumber: true })} />
              </FormField>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Regras de Valor Modal */}
      {regrasModal && <RegrasValorModal idcampeonato={regrasModal} onClose={() => setRegrasModal(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Campeonato"
        message={`Tem certeza que deseja excluir "${deleteTarget?.descricao}"?`}
        loading={deleteCampeonato.isPending}
      />
    </div>
  )
}

function RegrasValorModal({ idcampeonato, onClose }: { idcampeonato: number; onClose: () => void }) {
  const { data: regras, isLoading } = useRegrasValor(idcampeonato)
  const createRegra = useCreateRegraValor()
  const deleteRegra = useDeleteRegraValor()
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const form = useForm<RegraValorFormData>({
    resolver: zodResolver(regraValorSchema),
    defaultValues: { operador: '=', quantidade: 1, valor: 0, ordem_aplicacao: 1 },
  })

  async function handleAdd(formData: RegraValorFormData) {
    try {
      await createRegra.mutateAsync({ idcampeonato, data: formData })
      form.reset({ operador: '=', quantidade: 1, valor: 0, ordem_aplicacao: (regras?.length ?? 0) + 1 })
      setAlert({ type: 'success', message: 'Regra adicionada' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro' })
    }
  }

  return (
    <Modal open title="Regras de Valor de Inscricao" onClose={onClose} size="lg">
      {alert && <div className="mb-4"><Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} /></div>}
      <form onSubmit={form.handleSubmit(handleAdd)} className="grid grid-cols-4 gap-3 mb-4 items-end">
        <FormField label="Operador">
          <select className="w-full px-3 py-2 border border-compumaq-dourado/25 rounded-lg text-sm" {...form.register('operador')}>
            {['=', '>', '>=', '<', '<='].map(op => <option key={op} value={op}>{op}</option>)}
          </select>
        </FormField>
        <FormField label="Quantidade" error={form.formState.errors.quantidade?.message}>
          <input className="w-full px-3 py-2 border border-compumaq-dourado/25 rounded-lg text-sm" type="number" min={1} {...form.register('quantidade', { valueAsNumber: true })} />
        </FormField>
        <FormField label="Valor (R$)" error={form.formState.errors.valor?.message}>
          <input className="w-full px-3 py-2 border border-compumaq-dourado/25 rounded-lg text-sm" type="number" step="0.01" min={0} {...form.register('valor', { valueAsNumber: true })} />
        </FormField>
        <FormField label="Ordem">
          <input className="w-full px-3 py-2 border border-compumaq-dourado/25 rounded-lg text-sm" type="number" min={1} {...form.register('ordem_aplicacao', { valueAsNumber: true })} />
        </FormField>
        <div className="col-span-4 flex justify-end">
          <button type="submit" disabled={createRegra.isPending}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
            + Adicionar Regra
          </button>
        </div>
      </form>
      {isLoading ? <Spinner /> : !regras?.length ? (
        <p className="text-center text-compumaq-textoSuave py-4 text-sm">Nenhuma regra cadastrada</p>
      ) : (
        <table className="w-full text-sm border border-compumaq-dourado/25 rounded-lg overflow-hidden">
          <thead className="bg-compumaq-preto">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Ordem</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Operador</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Quantidade</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Valor</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-compumaq-dourado/25">
            {regras.map((r) => (
              <tr key={r.idregra_valor_inscricao}>
                <td className="px-3 py-2">{r.ordem_aplicacao}</td>
                <td className="px-3 py-2 font-mono">{r.operador}</td>
                <td className="px-3 py-2">{r.quantidade}</td>
                <td className="px-3 py-2">R$ {parseFloat(r.valor).toFixed(2)}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deleteRegra.mutate({ id: r.idregra_valor_inscricao, idcampeonato })}
                    className="text-xs text-red-600 hover:text-red-800">Remover</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  )
}
